import { useLanguage } from '@hooks/useLanguage';
import { OauthProvider } from '@shared/enums';
import { IdGovUaLogo } from '@shared/ui/IdGovUaLogo';
import { transliterate } from '@shared/utils/strings';
import clsx from 'clsx';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Application } from './types';

type ApplicationCardAddressProps = {
  address: Application['customer']['address'];
  oauth?: OauthProvider[];
} & React.HTMLAttributes<HTMLDivElement>;

function useApplicationCardAddressState({ address, className = '', oauth = [] }: ApplicationCardAddressProps) {
  const { t } = useTranslation();
  const language = useLanguage();

  const regionName = useMemo(() => transliterate(address.regionName, language), [address.regionName, language]);
  const district = useMemo(() => transliterate(address.district, language), [address.district, language]);
  const localityName = useMemo(() => transliterate(address.localityName, language), [address.localityName, language]);

  const isIdGovUaUser = oauth.includes(OauthProvider.IdGovUa);

  const wrapperClass = clsx(className, isIdGovUaUser && 'flex');

  return {
    firstLine: [t(regionName), district].filter(Boolean).join(', ') || t('no-description'),
    secondLine: localityName,
    isIdGovUaUser,
    wrapperClass,
  } as const;
}

export const ApplicationCardAddress = (props: ApplicationCardAddressProps) => {
  const { firstLine, secondLine, isIdGovUaUser, wrapperClass } = useApplicationCardAddressState(props);
  return (
    <div className={wrapperClass}>
      <div>
        <div className="text-black">{firstLine}</div>
        {secondLine && <div>{secondLine}</div>}
      </div>
      <IdGovUaLogo isLogoVisible={isIdGovUaUser} className="ml-6 mt-[2px]" />
    </div>
  );
};

// IdGovUaLogo
